// leafGeometry.js — the blade/maple/blossom geometry factories for broad leaves.
//
// All broad-leaf types (everything except pine needles) share the instanced-leaf
// pipeline; they differ only in the blade geometry built here and, for cherry,
// the default colour (handled in Branch.createDeciduousLeaves). Pinnate types (a
// single blade with a serrate margin) are driven by the LEAF_SHAPES table; maple
// and cherry build radial geometry of their own. makeLeafGeometry() is the entry
// point — it dispatches on parameters.leafType and returns one merged buffer (so
// the foliage draws as a single instanced mesh) whose attachment is at the local
// origin with the body extending up +Y.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { parameters } from './core.js';

// The pinnate types (deciduous, willow) are described by this table:
//   length/width  – blade proportions (× the base size)
//   teeth         – marginal tooth count
//   skew          – tooth-apex bias toward the tip (0.5 = symmetric, higher = raked)
//   toothOut/In   – how far teeth project / notches cut
//   crease        – central midrib fold depth
const LEAF_SHAPES = {
  deciduous: { length: 1.0, width: 1.0,  teeth: 6,  skew: 0.7,  toothOut: 0.18, notchIn: 0.10, crease: 0.08 },
  willow:    { length: 1.9, width: 0.34, teeth: 13, skew: 0.85, toothOut: 0.10, notchIn: 0.05, crease: 0.05 },
};

// Build the foliage geometry for the current leaf type. Pine is handled
// elsewhere (needles); every other type produces one merged buffer (so it's a
// single instanced draw) whose attachment is at the local origin, with the body
// extending up +Y. Pinnate types (deciduous, willow) use the blade builder;
// maple and cherry build radial geometry.
export function makeLeafGeometry() {
  if (parameters.leafType === 'maple') return makeMapleGeometry();
  if (parameters.leafType === 'cherry') return makeBlossomGeometry();
  return makeBladeGeometry(LEAF_SHAPES[parameters.leafType] || LEAF_SHAPES.deciduous);
}

// A thin low-poly cone petiole/pedicel from the attachment point (origin) up to
// `length`, tapering to a point. Non-indexed so it merges with the (non-indexed)
// leaf bodies. `radius` is the wide end, at the branch.
function makeStemCone(length, radius) {
  const g = new THREE.CylinderGeometry(0, radius, length, 5, 1).toNonIndexed();
  g.translate(0, length / 2, 0);
  return g;
}

// Pinnate blade (deciduous/willow): a symmetric silhouette with a serrate margin
// whose teeth point toward the tip, folded along a raised central midrib, on a
// cone petiole. `cfg` (from LEAF_SHAPES) sets the proportions and tooth profile.
function makeBladeGeometry(cfg) {
  const base = 2.25 * (parameters.foliageSize / 15);
  const len = base * cfg.length;
  const bladeBaseY = len * 0.15;     // blade slid down close to the attachment
  const stemRadius = base * 0.025;   // petiole thickness
  const halfMax = len * 0.34 * cfg.width;
  // Smooth half-width profile (clamped so a tip-skewed tooth can't run past s=1).
  const hw = (s) => halfMax * Math.pow(Math.sin(Math.PI * Math.min(Math.max(s, 0), 1)), 0.75);

  const right = []; // right-edge points [x, y], base -> tip
  for (let k = 0; k < cfg.teeth; k++) {
    const sN = k / cfg.teeth;                          // notch at the base side
    const sA = Math.min((k + cfg.skew) / cfg.teeth, 0.999); // apex skewed toward the tip
    if (k > 0) right.push([hw(sN) * (1 - cfg.notchIn), len * sN]);
    right.push([hw(sA) * (1 + cfg.toothOut), len * sA]);
  }

  // Folded mesh: a raised central spine (midrib) runs base->tip while each margin
  // point drops to z=0, so the cross-section is a shallow tent — a subtle crease.
  // It fades to nothing at base and tip via sin, so only the middle folds.
  const CREASE = len * cfg.crease;
  const creaseZ = (y) => CREASE * Math.sin(Math.PI * (y / len));
  const chain = [[0, 0], ...right, [0, len]]; // full right margin, base -> tip

  const positions = [];
  const uvs = [];
  const pushVert = (x, y, z) => {
    positions.push(x, y, z);
    uvs.push(0.5 + x / (2 * halfMax), y / len);
  };
  for (let j = 0; j < chain.length - 1; j++) {
    const [x0, y0] = chain[j];
    const [x1, y1] = chain[j + 1];
    const z0 = creaseZ(y0), z1 = creaseZ(y1);
    // Right half: spine (0,y,z) out to the margin (x,y,0).
    pushVert(0, y0, z0); pushVert(x0, y0, 0); pushVert(x1, y1, 0);
    pushVert(0, y0, z0); pushVert(x1, y1, 0); pushVert(0, y1, z1);
    // Left half: mirror across the spine (reversed winding so it faces out).
    pushVert(0, y0, z0); pushVert(-x1, y1, 0); pushVert(-x0, y0, 0);
    pushVert(0, y0, z0); pushVert(0, y1, z1); pushVert(-x1, y1, 0);
  }

  const bladeGeom = new THREE.BufferGeometry();
  bladeGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  bladeGeom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  bladeGeom.computeVertexNormals();
  bladeGeom.translate(0, bladeBaseY, 0);

  const stemGeom = makeStemCone(bladeBaseY + len * 0.3, stemRadius);
  const geom = mergeGeometries([stemGeom, bladeGeom]);
  bladeGeom.dispose();
  stemGeom.dispose();
  return geom;
}

// Radial leaf body: a triangle fan from a single centre vertex out to a margin
// sampled by `radiusFn(angle)` over [a0, a1]. The fan lies in the plane spanned
// by unit axes `u` and `v` (so callers can put it in-line with the stem or
// perpendicular to it), centred at `center`. `domeFn(r, a)` offsets EVERY vertex
// (centre and rim) along the plane normal — by radius for a bowl/dome, and/or by
// angle for radial pleats/folds — rather than just dimpling the centre. `R`
// normalises UVs.
// Returns a non-indexed BufferGeometry.
function makeRadialGeometry(R, center, u, v, domeFn, a0, a1, radiusFn, SEG = 60) {
  const positions = [];
  const uvs = [];
  const n = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]; // unit normal
  const pt = (a, r) => {
    const o = domeFn(r, a);
    return [
      center[0] + (u[0] * Math.cos(a) + v[0] * Math.sin(a)) * r + n[0] * o,
      center[1] + (u[1] * Math.cos(a) + v[1] * Math.sin(a)) * r + n[1] * o,
      center[2] + (u[2] * Math.cos(a) + v[2] * Math.sin(a)) * r + n[2] * o,
    ];
  };
  const oc = domeFn(0, a0);
  const cx = center[0] + n[0] * oc, cy = center[1] + n[1] * oc, cz = center[2] + n[2] * oc;
  for (let i = 0; i < SEG; i++) {
    const aA = a0 + (a1 - a0) * (i / SEG);
    const aB = a0 + (a1 - a0) * ((i + 1) / SEG);
    const pA = pt(aA, radiusFn(aA));
    const pB = pt(aB, radiusFn(aB));
    positions.push(cx, cy, cz); uvs.push(0.5, 0.5);
    positions.push(pA[0], pA[1], pA[2]); uvs.push(0.5 + Math.cos(aA) * 0.5, 0.5 + Math.sin(aA) * 0.5);
    positions.push(pB[0], pB[1], pB[2]); uvs.push(0.5 + Math.cos(aB) * 0.5, 0.5 + Math.sin(aB) * 0.5);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.computeVertexNormals();
  return g;
}

// Palmate Japanese maple (Acer palmatum) leaf: a 7-lobed radial body whose lobes
// are long, narrow and pointed with finely serrate margins and deeply-cut sinuses.
// The central lobe is longest; lobes shorten toward the base (a length envelope)
// so the lowest pair are small basal lobes flanking a cordate notch where the
// petiole enters. Lies in the stem plane (XY), slightly cupped, on a cone petiole.
function makeMapleGeometry() {
  const base = 2.25 * (parameters.foliageSize / 15);
  const R = base * 1.15;
  const attachY = R * 0.85;
  const LOBES = 7;
  const radiusFn = (a) => {
    const phi = a - Math.PI / 2;                              // 0 = straight up
    // cos(7φ/2) gives 7 lobes peaking at φ = 0, ±2π/7, ±4π/7, ±6π/7, with sinuses
    // (incl. one straight down). Exponent > 1 narrows each lobe to a sharp point;
    // the 0.26/0.60 split cuts the sinuses deep, as in palmatum.
    const c = Math.abs(Math.cos(LOBES * phi / 2));
    const lobe = Math.pow(c, 2.0);
    // A high-exponent spike centred on each lobe peak elongates the very tip into
    // a fine drawn-out point without fattening the body of the lobe.
    const tip = Math.pow(c, 9);
    const serr = 1 + 0.11 * Math.sin(phi * 44);              // deeper marginal teeth
    // Length envelope: central lobe longest, lobes shrink toward the base so the
    // lowest pair read as the small basal lobes of a palmate leaf.
    const env = 0.45 + 0.55 * Math.pow((1 + Math.cos(phi)) / 2, 0.6);
    // Cordate base: pull the margin in only around straight-down so the two basal
    // lobes flank a smooth rounded notch where the petiole enters (no hard V).
    let d = a + Math.PI / 2;                                  // angle from straight down
    d = Math.atan2(Math.sin(d), Math.cos(d));
    const notch = Math.exp(-(d / 0.45) * (d / 0.45));
    return R * env * (0.26 + 0.56 * lobe + 0.18 * tip) * (1 - 0.55 * notch) * serr;
  };
  // Full sweep — the cordate notch above replaces the old petiole wedge.
  const a0 = -Math.PI / 2;
  const a1 = a0 + Math.PI * 2;
  // Palmate pleat: instead of a flat disc, fold the surface radially so each lobe
  // rides up along its midrib (|cos(7φ/2)| ≈ 1) while the sinuses between lobes
  // dip the other way — the characteristic creased, folded-fan look of a maple.
  // The fold opens with radius (nothing at the centre, deepest at the tips) and a
  // mild overall centre cup keeps the heart of the leaf slightly recessed.
  const FOLD = R * 0.16;
  const domeFn = (r, a) => {
    const phi = a - Math.PI / 2;
    const ridge = Math.pow(Math.abs(Math.cos(LOBES * phi / 2)), 1.5); // 1 at midribs, 0 at sinuses
    const t = Math.min(r / R, 1);
    const pleat = (ridge - 0.5) * 2 * FOLD * Math.pow(t, 1.2);        // +FOLD midrib, -FOLD sinus
    const cup = -R * 0.05 * (1 - t);                                  // gentle centre cup
    return pleat + cup;
  };
  const body = makeRadialGeometry(R, [0, attachY, 0], [1, 0, 0], [0, 1, 0], domeFn, a0, a1, radiusFn, 224);
  const stemGeom = makeStemCone(attachY - R * 0.12, base * 0.03);
  const geom = mergeGeometries([stemGeom, body]);
  body.dispose();
  stemGeom.dispose();
  return geom;
}

// Cherry blossom: five rounded, tip-notched petals. The flower lies in the plane
// perpendicular to the stem (XZ), so the stem meets its centre at a right angle
// like a T; the centre is recessed toward the branch so the bloom cups outward,
// and a per-vertex colour gradient warms the heart of the flower.
function makeBlossomGeometry() {
  const base = 2.25 * (parameters.foliageSize / 15);
  const R = base * 0.8;
  const attachY = R * 0.9;
  // cos(5a) gives five petals; the -cos(10a) term carves a small notch into each
  // petal tip (the characteristic sakura petal) and deepens the sinuses.
  const radiusFn = (a) => R * (0.6 + 0.4 * Math.cos(5 * a) - 0.12 * Math.cos(10 * a));

  // Petals in the XZ plane (u = +X, v = +Z), perpendicular to the +Y stem. The
  // plane normal (u × v) is -Y, so a NEGATIVE dome offset lifts the petals toward
  // +Y: tips rise with radius while the centre stays put, so the bloom curls into
  // a bowl that cups upward/outward along the stem.
  const CUP = R * 0.4; // bowl depth (0 = flat flower)
  const domeFn = (r) => -CUP * Math.pow(Math.min(r / R, 1), 1.4);
  const body = makeRadialGeometry(R, [0, attachY, 0], [1, 0, 0], [0, 0, 1], domeFn, 0, Math.PI * 2, radiusFn);
  const stemGeom = makeStemCone(attachY, base * 0.022);
  const geom = mergeGeometries([stemGeom, body]);
  body.dispose();
  stemGeom.dispose();

  // Centre gradient: deep rose at the heart fading to white at the petal tips.
  // This multiplies the per-instance petal colour (vertexColors is enabled on the
  // material for cherry — see createDeciduousLeaves). Distance is measured from
  // the flower centre, so the thin stem below it reads as white.
  const pos = geom.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const accent = new THREE.Color(0xE0559A);
  const white = new THREE.Color(0xffffff);
  const tmp = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const dx = pos.getX(i);
    const dy = pos.getY(i) - attachY;
    const dz = pos.getZ(i);
    const tt = Math.min(Math.sqrt(dx * dx + dy * dy + dz * dz) / (R * 0.5), 1);
    tmp.copy(accent).lerp(white, tt);
    colors[i * 3] = tmp.r; colors[i * 3 + 1] = tmp.g; colors[i * 3 + 2] = tmp.b;
  }
  geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geom;
}
